import { ClipboardList, MessageSquare, PencilRuler } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMe } from "@/api/hooks/auth";
import { useOrgs } from "@/api/hooks/orgs";
import { useProjectsForOrgs, type ProjectOut } from "@/api/hooks/projects";
import { homePath } from "@/app/layouts/currentOrg";
import { PageLoading } from "@/components/Spinner";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { markWelcomeSeen } from "@/lib/welcome";

type Next = { title: string; body: string; to: string; action: string } | null;

/** First run after accepting an invite: who Codemonk is, your projects, the one next thing. */
export function WelcomePage() {
  const { t } = useTranslation();
  const me = useMe();
  const orgs = useOrgs();
  const results = useProjectsForOrgs((orgs.data ?? []).map((o) => o.slug));

  useEffect(() => {
    if (me.data) markWelcomeSeen(me.data.id);
  }, [me.data]);

  if (!me.data || orgs.isPending || results.some((r) => r.isPending)) return <PageLoading />;

  const projects: ProjectOut[] = results.flatMap((r) => r.data ?? []);
  const mostPending = [...projects].sort((a, b) => b.pending_required_count - a.pending_required_count)[0];
  const waiting = mostPending && mostPending.pending_required_count > 0 ? mostPending : undefined;
  const ready = waiting ? undefined : projects.find((p) => p.questionnaire_ready_to_submit);
  const ownerOrg = me.data.memberships.find((m) => m.role === "owner")?.organisation_slug;
  const firstName = me.data.name.split(/\s+/)[0] ?? me.data.name;

  let next: Next = null;
  if (ready) {
    next = {
      title: t("welcome.nextReady", { project: ready.name }),
      body: t("welcome.nextReadyBody"),
      to: `/${ready.organisation_slug}/${ready.slug}/questionnaire?resume=1`,
      action: t("project.nextStep.review"),
    };
  } else if (waiting) {
    next = {
      title: t("welcome.nextQuestions", { count: waiting.pending_required_count, project: waiting.name }),
      body: t("welcome.nextQuestionsBody"),
      to: `/${waiting.organisation_slug}/${waiting.slug}/questionnaire?resume=1`,
      action: t("welcome.startQuestionnaire"),
    };
  } else if (projects.length === 0 && ownerOrg) {
    next = {
      title: t("welcome.nextCreate"),
      body: t("welcome.nextCreateBody"),
      to: `/${ownerOrg}/projects/new`,
      action: t("welcome.createProject"),
    };
  } else if (projects[0]) {
    next = {
      title: t("welcome.nextOpen", { project: projects[0].name }),
      body: t("welcome.nextOpenBody"),
      to: `/${projects[0].organisation_slug}/${projects[0].slug}`,
      action: t("welcome.openProject"),
    };
  }

  const how = [
    { icon: ClipboardList, title: t("welcome.how1Title"), body: t("welcome.how1Body") },
    { icon: PencilRuler, title: t("welcome.how2Title"), body: t("welcome.how2Body") },
    { icon: MessageSquare, title: t("welcome.how3Title"), body: t("welcome.how3Body") },
  ];

  return (
    <div className="flex flex-col gap-8">
      <section className="rounded-xl bg-surface-inverse px-6 py-10 text-fg-inverse sm:px-10">
        <p className="text-sm font-semibold uppercase tracking-wide text-fg-inverse-muted">{t("welcome.eyebrow")}</p>
        <h1 className="mt-2 max-w-reading text-2xl text-fg-inverse sm:text-3xl">{t("welcome.title", { name: firstName })}</h1>
        <p className="mt-4 max-w-reading text-lg text-fg-inverse-muted">{t("welcome.about")}</p>
      </section>

      <div className="grid gap-6 lg:grid-cols-main">
        <Card className="border-l-4 border-l-brand">
          <CardContent className="flex flex-col gap-4 pt-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t("welcome.nextTitle")}</p>
            {next ? (
              <>
                <h2 className="text-xl">{next.title}</h2>
                <p className="text-fg-muted">{next.body}</p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild>
                    <Link to={next.to} className="no-underline">
                      {next.action}
                    </Link>
                  </Button>
                  <Button asChild variant="ghost">
                    <Link to={homePath(me.data)} className="no-underline">
                      {t("welcome.skip")}
                    </Link>
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl">{t("welcome.nextWait")}</h2>
                <p className="text-fg-muted">{t("welcome.nextWaitBody")}</p>
                <div>
                  <Button asChild variant="secondary">
                    <Link to={homePath(me.data)} className="no-underline">
                      {t("welcome.goHome")}
                    </Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6">
            <h2 className="text-xl">{t("welcome.yourProjects")}</h2>
            {projects.length > 0 ? (
              <ul className="flex flex-col divide-y divide-border">
                {projects.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-3">
                    <Link to={`/${p.organisation_slug}/${p.slug}`} className="min-w-0 truncate font-semibold">
                      {p.name}
                    </Link>
                    <StatusPill status={p.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-fg-muted">{t("welcome.noProjects")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <section aria-labelledby="how-title" className="flex flex-col gap-4">
        <h2 id="how-title">{t("welcome.howTitle")}</h2>
        <ol className="grid gap-4 md:grid-cols-3">
          {how.map((h, i) => (
            <li key={h.title} className="flex gap-4 rounded-lg border border-border bg-surface p-5 shadow-card">
              <span
                aria-hidden="true"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-subtle text-heading"
              >
                <h.icon className="h-5 w-5" />
              </span>
              <span className="flex flex-col gap-1">
                <span className="font-heading font-semibold text-heading">
                  {i + 1}. {h.title}
                </span>
                <span className="text-sm text-fg-muted">{h.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
